import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';

import { cn } from 'src/lib/utils';
import { toggleVariants } from 'src/components/ui/toggle';

const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants>>({
  size: 'default',
  variant: 'default',
});

function ToggleGroup({
  className,
  variant,
  size,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & VariantProps<typeof toggleVariants>) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      className={cn(
        'group/toggle-group flex w-fit items-center rounded-md data-[variant=outline]:shadow-xs',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size }}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        'min-w-0 flex-1 shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus:z-10 focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l',
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

function ToggleGroupCard({ children, className, ...props }: React.ComponentProps<typeof ToggleGroupPrimitive.Root>) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      className={cn('group/toggle-group space-y-2.5', className)}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant: 'default', size: 'custom' }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

function ToggleGroupCardItem({
  children,
  className,
  icon: Icon,
  title,
  description,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & {
  icon: React.ElementType;
  title: string;
  description?: string;
}) {
  return (
    <ToggleGroupPrimitive.Item className={cn(className)} {...props} asChild>
      <div
        className={cn(
          'group/toggle-group-card-item relative border-muted rounded-lg shadow-sm bg-white flex gap-3 py-4 pl-4 pr-9 select-none hover:bg-accent transition-colors data-[state=on]:border-secondary-foreground data-[state=on]:bg-brand-foreground data-[state=off]:cursor-pointer',
        )}
      >
        <div className="size-9 rounded-md bg-muted flex items-center justify-center shrink-0 text-foreground transition-colors group-data-[state=on]/toggle-group-card-item:bg-secondary-foreground group-data-[state=on]/toggle-group-card-item:text-white">
          <Icon className="size-4!" />
        </div>
        <div className="text-left not-has-[p]:flex not-has-[p]:flex-col not-has-[p]:justify-center">
          <h3 className="text-sm font-bold text-foreground">{title}</h3>
          {description && <p className="text-xs text-foreground/80">{description}</p>}
        </div>
        <div className="absolute top-[14px] right-[14px] size-4 rounded-full border-border border-1 bg-white flex items-center justify-center transition-colors group-data-[state=on]/toggle-group-card-item:border-primary group-data-[state=on]/toggle-group-card-item:bg-secondary-foreground group-data-[state=off]/toggle-group-card-item:[&>svg]:hidden">
          <Check className="size-3 text-white" />
        </div>
      </div>
    </ToggleGroupPrimitive.Item>
  );
}

export { ToggleGroup, ToggleGroupItem, ToggleGroupCard, ToggleGroupCardItem };
